import { useState } from 'react';
import {
  Clock,
  Loader2,
  Check,
  XCircle,
  ChevronDown,
  ChevronRight,
  Wrench,
  Bot,
} from 'lucide-react';
import { InlineReasoning } from './inline-reasoning';

interface SubAgentRunCardProps {
  args: Record<string, unknown>;
  result?: unknown;
  isError?: boolean;
}

interface SubAgentResult {
  status: string;
  agentName: string;
  agentId: string;
  reasoningText: string;
  responseText: string;
  toolCalls: Array<{ toolName: string; toolCallId: string }>;
  toolCallCount: number;
  durationMs?: number;
  error?: string;
  usage?: { inputTokens: number; outputTokens: number; totalTokens: number };
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'pending':
      return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
    case 'running':
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />;
    case 'completed':
      return <Check className="h-3.5 w-3.5 text-green-500" />;
    case 'failed':
      return <XCircle className="h-3.5 w-3.5 text-red-500" />;
    default:
      return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
  }
}

export function SubAgentRunCard({ args, result, isError }: SubAgentRunCardProps) {
  const [expanded, setExpanded] = useState(false);

  const agentName = (args.agentName as string) ?? 'Sub-Agent';
  const status = (args.status as string) ?? 'pending';
  const durationMs = args.durationMs as number | undefined;
  const toolCallCount = (args.toolCallCount as number) ?? 0;

  let parsed: SubAgentResult | undefined;
  if (result) {
    try {
      parsed = typeof result === 'string' ? JSON.parse(result) : (result as SubAgentResult);
    } catch { /* ignore */ }
  }

  const hasContent = parsed && (parsed.reasoningText || parsed.responseText || parsed.toolCalls?.length || parsed.error);

  return (
    <div className="ml-4 my-1 rounded-md border border-border bg-card text-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-foreground hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        {hasContent ? (
          expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )
        ) : (
          <div className="w-3.5" />
        )}

        <Bot className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />

        <span className="min-w-0 truncate text-xs font-medium">{agentName}</span>

        <span className="ml-auto flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          {toolCallCount > 0 && (
            <span className="flex items-center gap-0.5">
              <Wrench className="h-3 w-3" />
              {toolCallCount}
            </span>
          )}
          {durationMs != null && durationMs > 0 && (
            <span>{formatDuration(durationMs)}</span>
          )}
          <StatusIcon status={status} />
        </span>
      </button>

      {expanded && hasContent && parsed && (
        <div className="border-t border-border px-3 py-2 space-y-2">
          {parsed.reasoningText && (
            <div>
              <div className="text-[10px] font-medium uppercase text-muted-foreground/60 mb-0.5">
                Reasoning
              </div>
              <InlineReasoning text={parsed.reasoningText} />
            </div>
          )}

          {parsed.toolCalls?.length > 0 && (
            <div>
              <div className="text-[10px] font-medium uppercase text-muted-foreground/60 mb-0.5">
                Tool Calls
              </div>
              <div className="space-y-0.5">
                {parsed.toolCalls.map((tc) => (
                  <div key={tc.toolCallId} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Wrench className="h-3 w-3 shrink-0" />
                    <span className="font-mono">{tc.toolName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {parsed.responseText && (
            <div>
              <div className="text-[10px] font-medium uppercase text-muted-foreground/60 mb-0.5">
                Response
              </div>
              <div className="whitespace-pre-wrap text-xs text-foreground">
                {parsed.responseText}
              </div>
            </div>
          )}

          {parsed.usage && (
            <div>
              <div className="text-[10px] font-medium uppercase text-muted-foreground/60 mb-0.5">
                Usage
              </div>
              <div className="text-xs text-muted-foreground flex gap-3">
                <span>{parsed.usage.inputTokens.toLocaleString()} in</span>
                <span>{parsed.usage.outputTokens.toLocaleString()} out</span>
                <span>{parsed.usage.totalTokens.toLocaleString()} total</span>
              </div>
            </div>
          )}

          {(isError || parsed.error) && (
            <div>
              <div className="text-[10px] font-medium uppercase text-red-500 mb-0.5">
                Error
              </div>
              <div className="whitespace-pre-wrap text-xs text-red-600 dark:text-red-400">
                {parsed.error}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
